import { DescriptionCategoryRules } from "./description-category-rules";
import type { CategoryRuleFilter, DescriptionRule } from "@/lib/description-rules";
import { useEffect, useMemo, useState } from "react";
import { LoaderCircle, Pencil, Plus, Search, Tags, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { categoriesForDirection, categoryName, type FinanceCategory } from "@/lib/categories";
import { type Direction, type Language, type Transaction } from "@/lib/finance";
import type { UniqueCodeCategoryRule } from "@/lib/supabase-repository";
import { brandContext, businessUnitName, type BusinessUnit } from "@/lib/business-units";
import { BrandInput, CategoryInput, type CreateCategory } from "./classification-inputs";
import { categoryOrigin } from "@/lib/category-origin";

type CodeCategoryRulesProps = {
  descriptionRules: DescriptionRule[];
  onApplyHistorical: (filter: CategoryRuleFilter) => void;
  onSaveDescription: (rule: DescriptionRule) => Promise<void>;
  onDeleteDescription: (id: string) => Promise<void>;
  onManageCategories: () => void;
  onInspectTransaction: (row: Transaction) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rules: UniqueCodeCategoryRule[];
  transactions: Transaction[];
  language: Language;
  canEdit: boolean;
  categories: FinanceCategory[];
  units: BusinessUnit[];
  onCreateCategory: CreateCategory;
  onSave: (uniqueCode: string, direction: Direction, category: string, context: string, automatic: boolean, historical: boolean) => Promise<number>;
  onDelete: (uniqueCode: string, direction: Direction, context: string) => Promise<number>;
};

function ruleKey(uniqueCode: string, direction: Direction, context: string) {
  return `${direction}:${uniqueCode}:${context}`;
}

export function CodeCategoryRules({
  onApplyHistorical, onManageCategories, onInspectTransaction, descriptionRules, onSaveDescription, onDeleteDescription,
  open,
  onOpenChange,
  rules,
  transactions,
  language,
  canEdit,
  categories,
  units,
  onCreateCategory,
  onSave,
  onDelete,
}: CodeCategoryRulesProps) {
  const [search, setSearch] = useState("");
  const [uniqueCode, setUniqueCode] = useState("");
  const [direction, setDirection] = useState<Direction>("CREDIT");
  const [category, setCategory] = useState("");
  const [context, setContext] = useState("unassigned");
  const [automatic, setAutomatic] = useState(false);
  const [ruleView, setRuleView] = useState<"description" | "code">("description");
  const [busy, setBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState("");
  const text = language === "id" ? {
    title: "Aturan Kategori",
    description: "Aturan kode dan deskripsi dengan pratinjau serta prioritas konflik. Pilihan manual Owner tetap didahulukan.",
    code: "Kode unik",
    type: "Tipe",
    category: "Kategori",
    credit: "Kredit",
    debit: "Debet",
    save: "Simpan aturan",
    update: "Perbarui aturan",
    search: "Cari kode atau kategori",
    affected: "transaksi terkait",
    empty: "Belum ada aturan yang cocok.",
    delete: "Hapus",
    confirmDelete: "Konfirmasi hapus",
    cancel: "Batal",
    saved: "Aturan tersimpan",
    removed: "Aturan dihapus; transaksi lama tetap dipertahankan",
    invalid: "Masukkan kode tiga digit dan pilih kategori.",
    readOnly: "Anda dapat melihat aturan, tetapi hanya Owner yang dapat mengubahnya.",
  } : {
    title: "Category Rules",
    description: "Code and description rules with previews and conflict priorities. Owner manual choices take precedence.",
    code: "Unique code",
    type: "Type",
    category: "Category",
    credit: "Credit",
    debit: "Debit",
    save: "Save rule",
    update: "Update rule",
    search: "Search code or category",
    affected: "matching transactions",
    empty: "No matching rules.",
    delete: "Delete",
    confirmDelete: "Confirm delete",
    cancel: "Cancel",
    saved: "Rule saved",
    removed: "Rule deleted; historical transactions are preserved",
    invalid: "Enter a three-digit code and choose a category.",
    readOnly: "You can view rules, but only an Owner can change them.",
  };

  const options = categoriesForDirection(categories, direction).map((item) => item.slug);
  const unverified = useMemo(() => open ? transactions.filter((row) => categoryOrigin(row, rules).kind === "unverified") : [], [open, transactions, rules]);
  const existingRule = rules.find((rule) => rule.uniqueCode === uniqueCode && rule.direction === direction && rule.brandContext === context);
  const counts = useMemo(() => {
    const values = new Map<string, number>();
    if (!open) return values;
    transactions.forEach((transaction) => {
      const key = ruleKey(transaction.uniqueCode, transaction.direction, brandContext(transaction));
      values.set(key, (values.get(key) || 0) + 1);
    });
    return values;
  }, [open, transactions]);
  const filteredRules = useMemo(() => {
    const query = search.trim().toLowerCase();
    return rules.filter((rule) => !query || [rule.uniqueCode, categoryName(rule.category, language, categories), rule.direction]
      .join(" ").toLowerCase().includes(query));
  }, [rules, search, language, categories]);

  useEffect(() => {
    if (!options.includes(category)) setCategory("");
  }, [direction, category, options]);

  const resetForm = () => {
    setUniqueCode("");
    setCategory("");
    setDirection("CREDIT");
    setAutomatic(false);
  };

  const editRule = (rule: UniqueCodeCategoryRule) => {
    setUniqueCode(rule.uniqueCode);
    setDirection(rule.direction);
    setCategory(rule.category);
    setContext(rule.brandContext);
    setAutomatic(rule.automatic);
  };

  const save = async () => {
    if (!/^\d{3}$/.test(uniqueCode) || !category) {
      toast.error(text.invalid);
      return;
    }
    setBusy(true);
    try {
      await onSave(uniqueCode, direction, category, context, automatic, false);
      toast.success(language === "id" ? "Aturan tersimpan untuk input berikutnya. Transaksi lama dapat diproses melalui pratinjau." : "Rule saved for future input. Apply it to stored transactions through the preview.");
      resetForm();
    } catch {
      toast.error(language === "id" ? "Aturan gagal disimpan." : "The rule could not be saved.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (rule: UniqueCodeCategoryRule) => {
    setBusy(true);
    try {
      const affected = await onDelete(rule.uniqueCode, rule.direction, rule.brandContext);
      toast.success(`${text.removed}: ${affected} ${text.affected}`);
      setDeleteTarget("");
      if (uniqueCode === rule.uniqueCode && direction === rule.direction) resetForm();
    } catch {
      toast.error(language === "id" ? "Aturan gagal dihapus." : "The rule could not be deleted.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  return <Sheet open={open} onOpenChange={onOpenChange}>
    <SheetContent className="w-full overflow-y-auto sm:max-w-3xl">
      <SheetHeader className="border-b px-4 py-5 sm:px-6">
        <SheetTitle className="flex items-center gap-2"><Tags className="size-5" />{text.title}</SheetTitle>
        <SheetDescription>{text.description}</SheetDescription>
      </SheetHeader>
      <div className="space-y-5 p-4 sm:p-6"><Button variant="outline" onClick={onManageCategories}><Tags />{language === "id" ? "Kelola semua kategori" : "Manage all categories"}</Button><div className="grid grid-cols-2 gap-2"><Button variant={ruleView === "description" ? "default" : "outline"} onClick={() => setRuleView("description")}>{language === "id" ? "Berdasarkan deskripsi" : "By description"}</Button><Button variant={ruleView === "code" ? "default" : "outline"} onClick={() => setRuleView("code")}>{language === "id" ? "Berdasarkan kode" : "By code"}</Button></div><div hidden={ruleView !== "description"}><DescriptionCategoryRules onApplyHistorical={onApplyHistorical} rules={descriptionRules} codeRules={rules} categories={categories} units={units} rows={transactions} language={language} canEdit={canEdit} onSave={onSaveDescription} onDelete={onDeleteDescription} onInspect={onInspectTransaction} /></div><div hidden={ruleView !== "code"} className="space-y-5"><h3 className="font-semibold">{language === "id" ? "Aturan kode + arah + brand (prioritas 100)" : "Code + direction + brand rules (priority 100)"}</h3>
        <div className="rounded border bg-muted/15 p-3 text-sm"><p className="leading-6">{language === "id" ? "Kategori adalah nama pengelompokan. Aturan menentukan kapan kategori diterapkan otomatis. Kategori yang dipilih manual tidak harus memiliki aturan kode, baik untuk Debet maupun Kredit." : "Categories are grouping names; rules determine when they are applied automatically. Manually assigned categories do not need a code rule, for either Debit or Credit."}</p><Button variant="outline" className="mt-3" onClick={onManageCategories}><Tags />{language === "id" ? "Kelola semua kategori" : "Manage all categories"}</Button></div>
        {unverified.length > 0 && <details className="rounded border border-amber-400/60 bg-amber-500/5 p-3"><summary className="cursor-pointer text-sm font-semibold">{unverified.length} {language === "id" ? "transaksi dengan asal kategori yang perlu dikonfirmasi" : "transactions with unconfirmed category origins"}</summary><p className="mt-2 text-xs leading-5 text-muted-foreground">{language === "id" ? "Kategori tersimpan tanpa aturan otomatis aktif yang cocok. Data lama dipertahankan. Klik transaksi untuk memeriksa dan mengonfirmasi pilihan manual." : "Stored categories without a matching active automatic rule. Existing data is preserved. Open a transaction to review and confirm a manual choice."}</p><div className="mt-3 max-h-64 space-y-1 overflow-y-auto">{unverified.map((row) => <button key={row.id} type="button" className="block w-full rounded border p-2 text-left text-xs hover:bg-muted" onClick={() => onInspectTransaction(row)}><span className="font-semibold">{row.paymentRef || row.subject || row.id}</span><span className="mt-1 block">{row.direction === "DEBIT" ? text.debit : text.credit} · {categoryName(row.category, language, categories)} · Rp{row.amount.toLocaleString(language === "id" ? "id-ID" : "en-US")}</span></button>)}</div></details>}
        {canEdit ? <section className="rounded-2xl border bg-muted/20 p-4">
          <div className="grid gap-3 sm:grid-cols-[110px_140px_1fr]">
            <label><span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">{text.code}</span><Input inputMode="numeric" maxLength={3} placeholder="000" value={uniqueCode} onChange={(event) => setUniqueCode(event.target.value.replace(/\D/g, "").slice(0, 3))} /></label>
            <label><span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">{text.type}</span><Select value={direction} onValueChange={(value) => setDirection(value as Direction)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="CREDIT">{text.credit}</SelectItem><SelectItem value="DEBIT">{text.debit}</SelectItem></SelectContent></Select></label>
            <label><span className="mb-1.5 block text-xs font-semibold">{text.category}</span><CategoryInput value={category || "uncategorized"} direction={direction} categories={categories} language={language} onChange={setCategory} onCreate={onCreateCategory} /></label>
          </div>
          <div className="mt-3"><p className="mb-2 text-xs font-semibold">{language === "id" ? "Konteks brand" : "Brand context"}</p><BrandInput value={context} units={units} language={language} onChange={setContext} /><label className="mt-3 flex gap-3 text-sm"><input type="checkbox" className="size-5" checked={automatic} onChange={(event) => setAutomatic(event.target.checked)} />{language === "id" ? "Terapkan otomatis untuk konteks ini saja" : "Apply automatically only in this context"}</label><p className="mt-3 text-xs text-muted-foreground">{language === "id" ? "Simpan aturan dahulu. Terapkan ke data lama melalui tombol pratinjau pada aturan tersimpan." : "Save the rule first. Apply it to stored data using the saved rule’s preview button."}</p></div>
          <div className="mt-3 flex gap-2"><Button className="min-h-11 flex-1" disabled={busy || uniqueCode.length !== 3 || !category || category === "uncategorized"} onClick={() => void save()}>{busy ? <LoaderCircle className="animate-spin" /> : <Plus />}{existingRule ? text.update : text.save}</Button>{(uniqueCode || category) && <Button variant="outline" className="min-h-11" disabled={busy} onClick={resetForm}>{text.cancel}</Button>}</div>
        </section> : <p className="rounded-xl border bg-muted/25 p-4 text-sm text-muted-foreground">{text.readOnly}</p>}

        <div className="relative"><Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" /><Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={text.search} className="pl-9" /></div>

        <div className="space-y-2">
          {filteredRules.map((rule) => {
            const key = ruleKey(rule.uniqueCode, rule.direction, rule.brandContext);
            const deleting = deleteTarget === key;
            return <article key={key} className="flex flex-col gap-3 rounded-xl border p-4 sm:flex-row sm:items-center">
              <div className="flex min-w-0 flex-1 items-center gap-3">
                <Badge variant="outline" className="shrink-0 font-mono text-base">{rule.uniqueCode}</Badge>
                <div className="min-w-0"><p className="truncate text-sm font-semibold">{categoryName(rule.category, language, categories)}</p><p className="mt-1 text-xs text-muted-foreground">{businessUnitName(rule.brandContext, language, units)} · {rule.automatic ? "Auto" : language === "id" ? "Saran saja" : "Suggestions only"} · {rule.direction === "CREDIT" ? text.credit : text.debit} · {counts.get(key) || 0} {text.affected}</p></div>
              </div>
              {canEdit && <div className="flex flex-wrap gap-2"><Button size="sm" variant="outline" disabled={busy || !rule.automatic} onClick={() => onApplyHistorical({ kind: "code", uniqueCode: rule.uniqueCode, direction: rule.direction, brandContext: rule.brandContext })}>{language === "id" ? "Pratinjau data lama" : "Preview historical data"}</Button>
                {deleting ? <><Button size="sm" className="min-h-10" variant="destructive" disabled={busy} onClick={() => void remove(rule)}>{busy ? <LoaderCircle className="animate-spin" /> : <Trash2 />}{text.confirmDelete}</Button><Button size="sm" className="min-h-10" variant="outline" disabled={busy} onClick={() => setDeleteTarget("")}>{text.cancel}</Button></> : <><Button size="icon-lg" variant="ghost" onClick={() => editRule(rule)} aria-label={text.update}><Pencil /></Button><Button size="icon-lg" variant="ghost" className="text-destructive" onClick={() => setDeleteTarget(key)} aria-label={text.delete}><Trash2 /></Button></>}
              </div>}
            </article>;
          })}
          {!filteredRules.length && <div className="flex min-h-40 flex-col items-center justify-center text-center text-muted-foreground"><Tags className="mb-3 size-7 opacity-40" /><p className="text-sm">{text.empty}</p></div>}
        </div>
      </div></div>
    </SheetContent>
  </Sheet>;
}
